import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    ListItem,
    TagGroup,
    type TagGroupProps,
    type TagGroupTagProps,
    TagTreeEditor,
    type TagTreeEditorProps,
    type TestProps,
    type TreeNode,
} from '@direct-frontend/components';
import { createCustomTree, createMapFromArray, walkTreeTopDown } from '../../lib/array';

type Item = {
    id: string;
    parentId?: string;
    title: string;
};

export type TagTreeFieldProps = {
    value: string[];
    onChange: (value: string[]) => void;
    items: Item[];
    isLoading?: boolean;
    isDisabled?: boolean;
    isError?: boolean;
    validationState?: 'valid' | 'invalid';
    placeholder?: string;
    popupPlacement?: TagTreeEditorProps['popupPlacement'];
    emptySectionText?: string;
    getTagLabel?: (item: string) => string;
} & TestProps &
    Omit<
        TagGroupProps<unknown>,
        'tags' | 'isLoading' | 'isDisabled' | 'validationState' | 'placeholder' | 'tagEditorFactory'
    >;

type ChangedNodes = [nodeId: string, checked: boolean][];

export const TagTreeField: FC<TagTreeFieldProps> = ({
    value,
    onChange,
    items,
    isLoading,
    isDisabled,
    isError,
    validationState,
    placeholder,
    popupPlacement,
    emptySectionText = 'Ни чего не найдено',
    getTagLabel,
    'data-testid': dataTestId = 'TagTreeField',
    ...props
}) => {
    const mapItems = useMemo(() => (items ? createMapFromArray(items, (item) => [item.id, item]) : new Map()), [items]);

    const nodes = useMemo<TreeNode[]>(() => {
        if (!items.length) {
            return [];
        }

        return createCustomTree<Item, TreeNode>(
            items,
            (category) => {
                return {
                    id: category.id,
                    parentId: category.parentId,
                };
            },
            (item, id, children) => {
                return {
                    id,
                    label: item.title,
                    children,
                    isExpanded: true,
                };
            }
        );
    }, [items]);

    const changedNodes = useMemo<ChangedNodes>(() => {
        if (!items.length) {
            return [];
        }

        const set = new Set(value);

        if (set.size === 0) {
            return [];
        }

        return items.map(({ id }) => {
            if (set.has(id)) {
                return [id, true];
            }

            return [id, false];
        });
    }, [items, value]);

    const handleRemoveId = useCallback(
        (removedId: string) => {
            onChange(value.filter((id) => id !== removedId));
        },
        [onChange, value]
    );

    const tags = useMemo<TagGroupTagProps[]>(
        () =>
            value.map((id) => {
                return {
                    tag: getTagLabel?.(id) ?? mapItems.get(id)?.title ?? id,
                    onClose: () => {
                        handleRemoveId(id);
                    },
                };
            }),
        [getTagLabel, handleRemoveId, mapItems, value]
    );

    const refChangedNode = useRef<ChangedNodes>(changedNodes);

    useEffect(() => {
        refChangedNode.current = changedNodes;
    }, [changedNodes]);

    const handleChangeNodes = useCallback(
        (cb: (prev: ChangedNodes) => ChangedNodes) => {
            const changes = cb(refChangedNode.current);

            refChangedNode.current = changes;

            const map = new Map(changes);

            nodes.forEach((node) =>
                walkTreeTopDown<TreeNode, boolean>(
                    node,
                    ({ children }) => children ?? [],
                    (node, parentData) => {
                        if (parentData) {
                            if (!map.has(node.id)) {
                                map.set(node.id, true);
                            }
                        } else {
                            map.set(node.id, Boolean(map.get(node.id)));
                        }

                        return Boolean(map.get(node.id));
                    }
                )
            );

            onChange(
                Array.from(map.entries())
                    .filter(([, checked]) => checked)
                    .map(([id]) => id)
            );
        },
        [nodes, onChange]
    );

    return (
        <TagGroup
            {...props}
            data-testid={dataTestId}
            tags={tags}
            isLoading={isLoading}
            isDisabled={isDisabled}
            validationState={isError ? 'invalid' : validationState}
            placeholder={placeholder}
            maxInputHeight="170px"
            tagEditorFactory={(tagEditorProps) => (
                <TagTreeEditor
                    {...tagEditorProps}
                    nodes={nodes}
                    popupPlacement={popupPlacement}
                    emptySection={<ListItem>{emptySectionText}</ListItem>}
                    changedNodes={changedNodes}
                    setChangedNodes={handleChangeNodes}
                />
            )}
        />
    );
};
